import React from 'react';
import { UseFormRegister } from 'react-hook-form';
import { Form, Input, Button, Table, Select } from '@crisellevaldez/speculo-ui';
import { FileText, Link as LinkIcon, Pencil, Trash } from 'lucide-react';
import { BrandingFormData } from '../../../../../schemas/branding.schema';
import { ScrollableCell } from '../../../../common/ScrollableCell';
import { IconPill } from '../../../../common/IconPill';
import { LinkScheme, LINK_SCHEME_OPTIONS } from '../../../../../utils/urlUtils';

interface OrgLink {
  key: string;
  value: string;
  [key: string]: unknown;
}

interface FooterSettingsSectionProps {
  register: UseFormRegister<BrandingFormData>;
  orgLinks: OrgLink[];
  newLinkTitle: string;
  newLinkUrl: string;
  newLinkScheme: LinkScheme;
  editingLinkKey: string | null;
  onNewLinkTitleChange: (value: string) => void;
  onNewLinkUrlChange: (value: string) => void;
  onNewLinkSchemeChange: (value: LinkScheme) => void;
  onAddLink: () => void;
  onEditLink: (key: string) => void;
  onDeleteLink: (key: string) => void;
  onResetLink: () => void;
}

export const FooterSettingsSection: React.FC<FooterSettingsSectionProps> = ({
  register,
  orgLinks,
  newLinkTitle,
  newLinkUrl,
  newLinkScheme,
  editingLinkKey,
  onNewLinkTitleChange,
  onNewLinkUrlChange,
  onNewLinkSchemeChange,
  onAddLink,
  onEditLink,
  onDeleteLink,
  onResetLink,
}) => {
  const schemePlaceholder =
    LINK_SCHEME_OPTIONS.find((option) => option.value === newLinkScheme)?.placeholder ??
    'example.com';
  return (
    <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
      <div className="flex items-center gap-3 mb-3">
        <IconPill icon={FileText} />
        <h4 className="text-sm font-bold text-foreground">Footer Settings</h4>
      </div>

      <hr className="dark:border-white/10" />

      <div className="space-y-6 mt-5">
        <Form.Control>
          <Form.Label>License</Form.Label>
          <Input
            {...register('footer.license')}
            placeholder="e.g., Licensed Real Estate Agent #12345"
          />
          <Form.HelperText>License info displayed in the footer</Form.HelperText>
        </Form.Control>

        {/* Compliance Links */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <LinkIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <Form.Label className="mb-0">Compliance Links</Form.Label>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Compliance links appear at the footer of your site.
          </p>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr,minmax(120px,auto),2fr,auto] gap-4">
              <Input
                placeholder="Enter title"
                value={newLinkTitle}
                onChange={(e) => onNewLinkTitleChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (newLinkTitle && newLinkUrl) {
                      onAddLink();
                    }
                  }
                }}
              />
              <Select
                options={LINK_SCHEME_OPTIONS.map(({ value, label }) => ({ value, label }))}
                value={newLinkScheme}
                onChange={(value) => onNewLinkSchemeChange(value as LinkScheme)}
              />
              <div className="relative w-full">
                <Input
                  placeholder={schemePlaceholder}
                  className="w-full"
                  value={newLinkUrl}
                  onChange={(e) => onNewLinkUrlChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newLinkTitle && newLinkUrl) {
                        onAddLink();
                      }
                    }
                  }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onResetLink}>
                  {editingLinkKey ? 'Cancel' : 'Reset'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={onAddLink}
                  disabled={!newLinkTitle || !newLinkUrl}
                >
                  {editingLinkKey ? 'Update Link' : 'Add Link'}
                </Button>
              </div>
            </div>

            <div className="rounded-md">
              <div className="overflow-auto">
                {Array.isArray(orgLinks) && orgLinks.length > 0 ? (
                  <Table
                    columns={[
                      {
                        key: 'title',
                        header: 'Title',
                        width: '300px',
                        resizable: true,
                        cell: (item) => item.key,
                      },
                      {
                        key: 'url',
                        header: 'URL',
                        width: '300px',
                        cell: (item) => (
                          <ScrollableCell>
                            <span>{item.value}</span>
                          </ScrollableCell>
                        ),
                      },
                      {
                        key: 'actions',
                        header: 'Actions',
                        width: '100px',
                        isCentered: true,
                        cell: (item) => (
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onEditLink(item.key)}
                              style={{ padding: '10px' }}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onDeleteLink(item.key)}
                              className="text-white hover:bg-red-600 bg-red-700 border-0"
                              style={{ padding: '10px' }}
                            >
                              <Trash className="w-4 h-4" />
                            </Button>
                          </div>
                        ),
                      },
                    ]}
                    data={orgLinks}
                    keyExtractor={(item) => item.key}
                  />
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No compliance links added yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
